import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface SalaryData {
  input: string;
  processedData?: any;
  timestamp: Date;
  isValid?: boolean;
  validationMessage?: string;
}

interface SalaryState {
  // Current salary input
  salaryInput: string;

  // Processing state
  isProcessing: boolean;
  processingMessage?: string;

  // Salary data history
  salaryData: SalaryData | null;
  salaryHistory: SalaryData[];

  // Validation state
  isValidInput: boolean;
  validationErrors: string[];

  // Actions
  setSalaryInput: (input: string) => void;
  processSalary: (input: string) => Promise<void>;
  clearSalaryInput: () => void;
  validateSalaryInput: (input: string) => boolean;
  addSalaryData: (data: SalaryData) => void;
  clearSalaryData: () => void;
  setProcessing: (processing: boolean, message?: string) => void;
}

// Salary validation rules
const validateSalaryInput = (input: string): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  const trimmedInput = input.trim();
  const MAX_LENGTH = 255;

  if (trimmedInput.length > MAX_LENGTH) {
    errors.push(`Salary expectations must be ${MAX_LENGTH} characters or less.`);
    return { isValid: false, errors };
  }

  if (trimmedInput.length === 0) {
    errors.push('Salary input cannot be empty');
    return { isValid: false, errors };
  }

  if (trimmedInput.length < 3) {
    errors.push('Please provide a more detailed salary response');
    return { isValid: false, errors };
  }

  // Check for basic salary patterns (numbers, ranges, etc.)
  const hasNumbers = /\d/.test(trimmedInput);
  const hasCurrency = /[$£€¥₹]/.test(trimmedInput);
  const hasRangeIndicators = /(-|to|between|range|from)/i.test(trimmedInput);
  const hasSalaryKeywords = /(salary|compensation|pay|wage|income|k|thousand|million)/i.test(trimmedInput);

  // If it doesn't look like a salary response, suggest improvement
  if (!hasNumbers && !hasCurrency && !hasRangeIndicators && !hasSalaryKeywords) {
    errors.push('Please provide specific salary information (e.g., numbers, ranges, or compensation details)');
    return { isValid: false, errors };
  }

  return { isValid: true, errors: [] };
};

export const useSalaryStore = create<SalaryState>()(
  devtools(
    (set, get) => ({
      // Initial state
      salaryInput: '',
      isProcessing: false,
      salaryData: null,
      salaryHistory: [],
      isValidInput: false,
      validationErrors: [],

      // Actions
      setSalaryInput: (input: string) => {
        const validation = validateSalaryInput(input);
        set({
          salaryInput: input,
          isValidInput: validation.isValid,
          validationErrors: validation.errors
        });
      },

      processSalary: async (input: string) => {
        set({ isProcessing: true, validationErrors: [] });

        try {
          // Validate the salary input
          const validation = validateSalaryInput(input);

          if (!validation.isValid) {
            set({
              validationErrors: validation.errors,
              isProcessing: false
            });
            return;
          }

          // Process the salary (could involve API calls, etc.)
          await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate processing

          // Add to history
          set((state) => ({
            salaryHistory: [...state.salaryHistory, {
              input,
              timestamp: new Date(),
              processed: true
            }],
            isProcessing: false
          }));

        } catch (error) {
          set({
            validationErrors: ['Failed to process salary information'],
            isProcessing: false
          });
        }
      },

      clearSalaryInput: () => {
        set({
          salaryInput: '',
          isValidInput: false,
          validationErrors: []
        });
      },

      validateSalaryInput: (input: string) => {
        const validation = validateSalaryInput(input);
        set({
          isValidInput: validation.isValid,
          validationErrors: validation.errors
        });
        return validation.isValid;
      },

      addSalaryData: (data: SalaryData) => {
        set((state) => ({
          salaryData: data,
          salaryHistory: [...state.salaryHistory, data]
        }));
      },

      clearSalaryData: () => {
        set({
          salaryData: null,
          salaryHistory: [],
          salaryInput: '',
          isValidInput: false,
          validationErrors: []
        });
      },

      setProcessing: (processing: boolean, message?: string) => {
        set({
          isProcessing: processing,
          processingMessage: message
        });
      },
    }),
    {
      name: 'salary-store',
    }
  )
);
